import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actions as actionsUserCards } from 'appRedux/modules/UserCards';
import { debounce as _debounce } from 'lodash';
import { Loading, Message } from 'components';
import {
	initializeSDK,
	getCreditCardForm,
	getPaymentToken,
} from 'services/funpoint';
import { Cookies } from 'utils';

/** ---------------------------------------------------------------------------------------------
 * Constant
 * @type {string} SDK_URL       URL of FunPoint SDK
 */
// const SDK_URL =
// 	'https://ecpg-stage.funpoint.com.tw/Scripts/sdk-1.0.0.src.js?t=20210121100116'; // development
const SDK_URL =
	'https://ecpg.funpoint.com.tw/Scripts/sdk-1.0.0.src.js?t=20210121100116'; // production

/** ---------------------------------------------------------------------------------------------
 * Props
 * @type {string} display       Display state
 *  - block: Display
 *  - none: Hide
 *
 * @type {string} source        Source of the form
 *  - order: Payment page
 *  - profile: Profile page
 *
 * @type {Object} cardBound     Card bound
 *  - false: Not initialized yet
 *  - null: No card bound
 *  - object: Card bound
 *   @property {string} bindCardID Bind card ID
 *   @property {string} bincode Bincode (first six digits)
 *   @property {string} lastfour Last four digits
 *   @property {string} trade_type Trade type (must be funpoint)
 */
const FunPointForm = ({ display, source = 'order', cardBound = null }) => {
	/** ---------------------------------------------------------------------------------------------
	 * Basic
	 */
	const dispatch = useDispatch();

	/** ---------------------------------------------------------------------------------------------
	 * Redux
	 * @type {undefined | string | null} three_d_url    3D secure URL received from Backend
	 * - undefined: Not initialized yet
	 * - string: URL of the 3D secure
	 * - null: No 3D secure
	 *
	 * @type {string} funpoint_form_token    FunPoint form token received from Backend
	 */
	const { three_d_url, funpoint_form_token } = useSelector(state => ({
		three_d_url: state.UserCards.three_d_url,
		funpoint_form_token: state.UserCards.funpoint_form_token,
	}));

	/** ---------------------------------------------------------------------------------------------
	 * Ref
	 * @type {Object} formRef          Reference to the form element
	 */
	const formRef = useRef(null);

	/** ---------------------------------------------------------------------------------------------
	 * State
	 * @type {boolean} sdkLoaded       If SDK is loaded         (是否已加載 SDK)
	 * @type {boolean} initialized     If SDK is initialized    (是否已初始化 SDK)
	 * @type {boolean} formLoaded      If form is loaded        (是否已加載表單)
	 * @type {boolean} loading         If data is loading       (是否正在加載資料)
	 * @type {string}  error           Error message            (錯誤訊息)
	 */
	const [sdkLoaded, setSdkLoaded] = useState(false);
	const [initialized, setInitialized] = useState(false);
	const [formLoaded, setFormLoaded] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	/** ---------------------------------------------------------------------------------------------
	 * Function
	 * @type {Function} getForm     Inject form html from FunPoint SDK    (從 FunPoint SDK 獲取'綁定信用卡畫面'表單)
	 */
	const getForm = token => {
		setLoading(true);
		getCreditCardForm(token, err => {
			if (err) {
				setError('FORM_ERROR');
				console.log('Get form error:', err, token);
			} else {
				setFormLoaded(true);
				setError(null);
			}
			setLoading(false);
		});
	};

	/** ---------------------------------------------------------------------------------------------
	 * Function
	 * @type {Function} checkBindingCard     Check if the card is valid and bind the card    (檢查信用卡是否有效並綁定卡片)
	 */
	const checkBindingCard = () => {
		const onBindCard = () => {
			setLoading(true);
			// --------------------------------------------------------
			// 從 FunPoint SDK '取得付款代碼 (PayToken)'
			getPaymentToken((paymentInfo, err) => {
				if (err) {
					// --------------------------------------------------------
					// handle error: 取得付款代碼錯誤
					setError('CARD_ERROR');
					console.log('Payment token error:', err, paymentInfo);
				} else {
					// --------------------------------------------------------
					// handle success: 取得付款代碼成功，進行後續操作
					/**
					 * @type {string} BindCardPayToken     e.g "b6aa2744b8cb405d8a2228f7e14d9ab3" (綁定卡片的支付 token)
					 * @type {string} CVV                  e.g "222"                              (信用卡背面末三碼)
					 * @type {string} MerchantID           e.g "1000031"                          (商店 ID)
					 * @type {string} MerchantMemberID     e.g "U42241"                           (商店會員 ID)
					 * @type {string} PayToken                                                    (付款 token)
					 * @type {string} PaymentType          e.g "CreditCard"                       (付款方式)
					 */
					if (paymentInfo && paymentInfo.BindCardPayToken) {
						// --------------------------------------------------------
						// 信用卡有效，進行綁定卡片操作
						dispatch(
							actionsUserCards.addFunPointCardRequest({
								token: paymentInfo.BindCardPayToken,
							})
						);
					} else {
						// --------------------------------------------------------
						// 信用卡無效，顯示錯誤提示
						Message.SmallBox({
							title: `<i class='fa fa-exclamation-triangle' aria-hidden='true'></i> 請檢查付款卡片資料是否正確`,
							content: '',
							timeout: 3000,
						});
						setLoading(false);
					}
				}
			});
		};

		onBindCard();
	};

	/** ---------------------------------------------------------------------------------------------
	 * useEffect - 1. Import SDK script after container (#funpointPayment) rendered
	 */
	useEffect(() => {
		// --------------------------------------------------------
		// Step1: 如果表單未顯示 or 存在錯誤 or DOM 元素不存在，則不載入 SDK script
		if (display !== 'block' || error || !formRef.current) {
			return;
		}

		// --------------------------------------------------------
		// Step2: 檢查 SDK script 是否已載入，如果已載入則不再重複載入
		const sdkSet =
			document.querySelectorAll(`script[src='${SDK_URL}']`).length > 0;
		if (sdkSet) {
			return;
		}

		// --------------------------------------------------------
		// Step3: 創建一個 script 標籤並將 src 設置為 SDK_URL
		const elemScript = document.createElement('script');
		elemScript.src = SDK_URL;

		// --------------------------------------------------------
		// Step4: 載入 SDK 完成後更新狀態
		elemScript.onload = () => {
			// console.log('~~~載入 SDK script 成功~~~'); //FIXME:
			setSdkLoaded(true);
		};

		// --------------------------------------------------------
		// Step5: 將 script 插入到 <head> 中以載入 SDK
		document.getElementsByTagName('head')[0].appendChild(elemScript);
	}, [display, error]);

	/** ---------------------------------------------------------------------------------------------
	 * useEffect - 2. Initialise SDK after the SDK script is loaded
	 */
	useEffect(() => {
		// --------------------------------------------------------
		// Step1: 如果 SDK script 未載入 or 已初始化過 or 表單未顯示，則不初始化 SDK
		if (!sdkLoaded || initialized || display !== 'block') {
			return;
		}

		// --------------------------------------------------------
		// Step2: 開始初始化 SDK
		setLoading(true);
		initializeSDK(err => {
			if (err) {
				// handle error: 初始化錯誤
				setError('INIT_ERROR');
				console.log('SDK initialization error:', err);
			} else {
				// handle success: 初始化成功
				// console.log('~~~初始化 SDK 成功~~~'); //FIXME:
				setInitialized(true);
				setError(null);
			}

			setLoading(false);
		});
	}, [display, initialized, sdkLoaded]);

	/** ---------------------------------------------------------------------------------------------
	 * useEffect - 3. Load form if SDK is initialized, funpoint_form_token exists and no card is bound
	 */
	useEffect(() => {
		if (initialized && funpoint_form_token && cardBound === null) {
			// console.log('~~~載入表單~~~'); //FIXME:
			getForm(funpoint_form_token);
		}
	}, [cardBound, funpoint_form_token, initialized]);

	/** ---------------------------------------------------------------------------------------------
	 * useEffect - 4. Redirect to 3D secure
	 */
	useEffect(() => {
		if (three_d_url) {
			Cookies.remove('redirect_path', { path: '/' });
			window.location.replace(three_d_url);
		}
	}, [three_d_url]);

	// ---------------------------------------------------------------------------------------------

	return (
		<div className={`funpoint-form ${display}`}>
			{loading && <Loading />}

			{error ? (
				<div className="feedback margin-top-10vh text-center">
					<div className="feedback-title">
						<i className="txt-orange txt-5em elegant elegant-eleganticons-81" />

						<h1>信用卡驗證尚未完成，請重新整理後再操作一次</h1>
					</div>
				</div>
			) : (
				<>
					{/* 添加 FunPoint SDK 必要的 UI 元素 */}
					<div className="info-icon d-none"></div>
					<div className="tooltip-container d-none"></div>
					<div className="tooltip-close d-none"></div>

					{/* Form container */}
					<div id="funpointPayment" ref={formRef} />

					{/* Bind button */}
					{formLoaded && (
						<div className="btn-container">
							<button
								type="button"
								className="ua-btn default"
								onClick={_debounce(checkBindingCard, 300, {
									leading: true,
									trailing: false,
								})}
							>
								綁定
							</button>
						</div>
					)}
				</>
			)}
		</div>
	);
};

export { FunPointForm };
